import { create } from "zustand";
import { Screen } from "../types/enums";

//  placeholder store - only tracks the current screen for UI development purposes
interface AuthStore {
  sessionId: string;
  screen: Screen;
  darkMode: boolean;
  hasStarted: boolean;
  setSessionId: (sessionId: string) => void;
  setDarkMode: (isDark: boolean) => void;
  setScreen: (screen: Screen) => void;
  setHasStarted: (hasStarted: boolean) => void;
  nextScreen: () => void;
  resetScreen: () => void;
  deepClearSession: () => void;
  // Hardcoded data for views
  viewData: {
    appName: string;
    dataSourceName: string;
    information: {
      title: string;
      subheader: string;
    };
    sourceSelection: {
      title: string;
      subheader: string;
      sources: Array<{
        id: string;
        name: string;
        icon: string;
      }>;
      sourceOptions: Array<{
        value: string;
        label: string;
      }>;
    };
    dataSelection: {
      title: string;
      subheader: string;
      dataOptions: Array<{
        id: string;
        title: string;
        description: string;
      }>;
    };
    credential: {
      title: string;
      subheader: string;
    };

    proofGeneration: {
      title: string;
      subheader: string;
    };
    proofCompletion: {
      title: string;
      subheader: string;
      summaryData: {
        dataSource: string;
        dataType: string;
        proofId: string;
      };
    };
  };
}

window.addEventListener("unload", () => {
  // When we are in a popup, and it closes
  // we don't want to clear the sessionId
  // because we are in the OAuth flow that uses the popup
  // with the same url as the iFrame to communicate
  const isInPopup = window.opener !== null;
  if (!isInPopup) {
    useAuthStore.getState().deepClearSession();
  }
});

export const useAuthStore = create<AuthStore>((set, get) => ({
  sessionId: "",
  setSessionId: (sessionId: string) => set({ sessionId }),
  screen: Screen.PENDING,
  darkMode: false,
  hasStarted: false,
  setDarkMode: (isDark) => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    set({ darkMode: isDark });
  },
  setHasStarted: (hasStarted) => set({ hasStarted }),
  setScreen: (screen) => set({ screen }),
  nextScreen: () =>
    set((state) => ({
      screen:
        Object.values(Screen)[
          (Object.values(Screen).indexOf(state.screen) + 1) %
            Object.values(Screen).length
        ],
    })),
  deepClearSession: () => {
    sessionStorage.removeItem("pluto-session-id");
    sessionStorage.removeItem(`${get().sessionId}-screen`);
    set({ sessionId: "", screen: Screen.TIMEOUT, hasStarted: false });
  },
  resetScreen: () => set({ screen: Screen.INFORMATION, hasStarted: false }),
  viewData: {
    appName: "App Name",
    dataSourceName: "Twitter",
    information: {
      title: "Link your account instantly",
      subheader:
        "App Name uses Pluto to privately provide information about your App Name account.",
    },
    sourceSelection: {
      title: "Choose a data source to share with App Name",
      subheader: "Select a platform where you have an account",
      sources: [
        {
          id: "reddit",
          name: "Reddit",
          icon: "reddit",
        },
        {
          id: "spotify",
          name: "Spotify",
          icon: "spotify",
        },
        {
          id: "venmo",
          name: "Venmo",
          icon: "venmo",
        },
        {
          id: "twitter",
          name: "X",
          icon: "twitter",
        },
        {
          id: "coinbase",
          name: "Coinbase",
          icon: "coinbase",
        },
      ],
      sourceOptions: [
        { value: "reddit", label: "Reddit" },
        { value: "spotify", label: "Spotify" },
        { value: "venmo", label: "Venmo" },
        { value: "twitter", label: "X" },
        { value: "coinbase", label: "Coinbase" },
      ],
    },
    dataSelection: {
      title: "Select Twitter data",
      subheader: "Choose what information you want to share",
      dataOptions: [
        {
          id: "account_ownership",
          title: "Account Ownership",
          description:
            "Verify that you own this account without sharing any personal information.",
        },
        {
          id: "basic_profile",
          title: "Basic Profile",
          description:
            "Share your public profile information including username and profile picture.",
        },
        {
          id: "activity_history",
          title: "Activity History",
          description:
            "Share your recent activity history to verify account usage patterns.",
        },
      ],
    },
    credential: {
      title: "Login to Twitter",
      subheader: "Enter your Twitter credentials to continue",
    },

    proofGeneration: {
      title: "Generating Proof",
      subheader: "Please wait while we securely generate your proof",
    },
    proofCompletion: {
      title: "Success! Your proof has been generated",
      subheader: "Your Twitter account has been successfully verified",
      summaryData: {
        dataSource: "Twitter",
        dataType: "Account Ownership",
        proofId: "prf_28a9d7e5f6b3c1",
      },
    },
  },
}));
